// Embudos · lista + creación desde plantillas sencillas.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Modal, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { STEP_ICON, STEP_LABEL } from "./shared.ts";

interface FunnelRow { id: string; name: string; status: string; ownerName: string | null; createdAt: string; stepCount: number; stepTypes: string[] }
interface Template { key: string; name: string; description: string }


export function FunnelList(): JSX.Element {
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [tpl, setTpl] = useState("capture");

  const list = useQuery<{ rows: FunnelRow[] }>({ queryKey: ["mkt-funnels"], queryFn: () => api.get("/mkt/funnels") });
  const templates = useQuery<{ rows: Template[] }>({ queryKey: ["mkt-funnel-templates"], queryFn: () => api.get("/mkt/funnels/templates"), enabled: open });
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");
  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>("/mkt/funnels", { name: name.trim(), template: tpl }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["mkt-funnels"] }); void qc.invalidateQueries({ queryKey: ["mkt-landings"] }); nav(r.id); },
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/funnels/${id}`),
    onSuccess: () => { toast.success("Embudo eliminado."); void qc.invalidateQueries({ queryKey: ["mkt-funnels"] }); }, onError: onErr,
  });
  const rows = list.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="text-lg font-semibold text-ink">Embudos</h2>
        {admin && <Button variant="primary" onClick={() => setOpen(true)}><Plus size={16} strokeWidth={1.75} /> Nuevo embudo</Button>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo embudo"
        footer={<><Button onClick={() => setOpen(false)}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Crear</Button></>}>
        <div className="space-y-3">
          <Field label="Nombre" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoFocus placeholder="Ej.: Formación 2026" /></Field>
          <div>
            <div className="label">Plantilla</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(templates.data?.rows ?? []).map((t) => (
                <button key={t.key} type="button" onClick={() => setTpl(t.key)}
                  className={`text-left rounded-md border p-3 ${tpl === t.key ? "border-primary bg-primary-soft" : "border-line hover:bg-surface-hover"}`}>
                  <div className="text-sm font-medium text-ink">{t.name}</div>
                  <div className="text-xs text-ink-muted">{t.description}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-2">Las plantillas con landing crean landings en borrador (carpeta «Embudos») listas para editar.</p>
          </div>
        </div>
      </Modal>

      <Card className="overflow-hidden">
        {list.isLoading ? <TableSkeleton rows={4} cols={5} />
          : list.isError ? <ErrorState message="No se pudieron cargar los embudos." onRetry={() => void list.refetch()} />
          : !rows.length ? <EmptyState title="Sin embudos" body="Un embudo encadena landings, formularios, encuestas y un pipeline de ventas para medir la conversión paso a paso." />
          : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full">
                <thead><tr><th className="th">Nombre</th><th className="th">Pasos</th><th className="th">Estado</th><th className="th">Propietario</th><th className="th">Creado</th><th className="th" /></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-hover cursor-pointer" onClick={() => nav(r.id)}>
                      <td className="td font-medium text-ink">{r.name}</td>
                      <td className="td">
                        <div className="flex items-center gap-1 text-ink-muted">
                          {r.stepTypes.map((t, i) => { const I = STEP_ICON[t as keyof typeof STEP_ICON] ?? Globe; return <span key={i} className="inline-flex items-center gap-1">{i > 0 && <span>→</span>}<I size={14} strokeWidth={1.75} aria-label={STEP_LABEL[t]} /></span>; })}
                          {!r.stepCount && <span className="text-xs">Sin pasos</span>}
                        </div>
                      </td>
                      <td className="td"><Badge color={r.status === "published" ? "green" : "gray"}>{r.status === "published" ? "Publicado" : "Borrador"}</Badge></td>
                      <td className="td whitespace-nowrap">{r.ownerName ?? "—"}</td>
                      <td className="td whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      <td className="td" onClick={(e) => e.stopPropagation()}>
                        {admin && <button className="btn-ghost btn-sm text-danger" title="Eliminar" onClick={() => { if (window.confirm(`¿Eliminar el embudo «${r.name}»? Sus landings y formularios no se borran.`)) del.mutate(r.id); }}><Trash2 size={14} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
